import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    createQuote,
    downloadQuotePdf,
    emailQuote,
    getQuote,
    listQuotes,
    replaceQuoteRequirements,
    updateQuote,
} from './api'
import {quoteKeys} from './query-keys'
import type {CreateQuoteInput, QuoteRequirementWriteInput, UpdateQuoteInput} from './schemas'

export function useQuotes(options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: quoteKeys.list(),
        queryFn: listQuotes,
        enabled: options?.enabled ?? true,
    })
}

export function useQuote(id: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: quoteKeys.detail(id),
        queryFn: () => getQuote(id),
        enabled: options?.enabled ?? true,
    })
}

export function useDownloadQuotePdf(id: string) {
    return useMutation({
        mutationKey: quoteKeys.pdf(id),
        mutationFn: () => downloadQuotePdf(id),
        retry: false,
    })
}

export function useCreateQuote() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateQuoteInput) => createQuote(input),
        onSuccess: (quote) => {
            void queryClient.invalidateQueries({queryKey: quoteKeys.list()})
            queryClient.setQueryData(quoteKeys.detail(quote.id), quote)
        },
    })
}

export function useUpdateQuote() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, input}: {id: string; input: UpdateQuoteInput}) => updateQuote(id, input),
        onSuccess: (quote) => {
            queryClient.setQueryData(quoteKeys.detail(quote.id), quote)
            void queryClient.invalidateQueries({queryKey: quoteKeys.list()})
        },
    })
}

export function useReplaceQuoteRequirements() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            id,
            requirements,
        }: {
            id: string
            requirements: QuoteRequirementWriteInput[]
        }) => replaceQuoteRequirements(id, requirements),
        onSuccess: (quote) => {
            queryClient.setQueryData(quoteKeys.detail(quote.id), quote)
            void queryClient.invalidateQueries({queryKey: quoteKeys.list()})
        },
    })
}

export function useEmailQuote(quoteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => emailQuote(quoteId),
        onSuccess: (quote) => {
            queryClient.setQueryData(quoteKeys.detail(quote.id), quote)
            void queryClient.invalidateQueries({queryKey: quoteKeys.list()})
        },
    })
}
